import { useRef } from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { Text, theme } from '@socrait/react';

export type Tab = 'overview' | 'customMessages' | 'dataLog';

type TabBarProps = {
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
};

const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'customMessages', label: 'Custom Messages' },
  { key: 'dataLog', label: 'Data Log' },
];

export function TabBar({ activeTab, onTabChange }: TabBarProps) {
  const tabRefs = useRef<(HTMLElement | null)[]>([]);

  const handleKeyDown = (e: KeyboardEvent, index: number) => {
    let nextIndex: number | null = null;

    switch (e.key) {
      case 'ArrowRight':
        nextIndex = (index + 1) % TABS.length;
        break;
      case 'ArrowLeft':
        nextIndex = (index - 1 + TABS.length) % TABS.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = TABS.length - 1;
        break;
    }

    if (nextIndex !== null) {
      e.preventDefault();
      onTabChange(TABS[nextIndex]!.key);
      tabRefs.current[nextIndex]?.focus?.();
    }
  };

  return (
    <View style={styles.container} accessibilityRole="tablist">
      {TABS.map(({ key, label }, index) => (
        <Pressable
          key={key}
          ref={(el) => {
            tabRefs.current[index] = el as unknown as HTMLElement;
          }}
          onPress={() => onTabChange(key)}
          style={styles.tab}
          accessibilityRole="tab"
          accessibilityState={{ selected: activeTab === key }}
          {...({ onKeyDown: (e: KeyboardEvent) => handleKeyDown(e, index) } as object)}>
          <Text style={styles.label}>{label}</Text>
          {activeTab === key && <View style={styles.underline} />}
        </Pressable>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xlarge,
  },
  tab: {
    alignItems: 'center',
  },
  label: {
    fontSize: theme.fontSizes.medium,
    fontWeight: theme.fontWeights.bold,
  },
  underline: {
    position: 'absolute',
    bottom: -theme.spacing.small,
    left: 0,
    right: 0,
    height: 2,
    backgroundColor: theme.colors.navy,
  },
});
